import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';

import { UnifiedCard } from '../components/ui/unified-card';
import { ApplicationForm } from '../components/ApplicationForm';
import { ArchiveApplication } from '../components/ArchiveApplication';
import { DeleteApplication } from '../components/DeleteApplication';
import { RestoreApplication } from '../components/RestoreApplication';
import { Paginator } from '../components/Paginator';
import {
  Plus,
  Search,
  Filter,
  FileText,
  FolderOpen,
  Package,
  Activity,
  Eye,
  Edit,
  Archive,
  Trash2,
  Heart,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  ArchiveRestore,
  User,
  Clock,
  Calendar
} from 'lucide-react';
import { useState, useMemo } from 'react';
import {  getDevelopmentPhaseColor, getPriorityColor, getStatusColor, isSubscriptionValid } from '@/helpers/common';
import { useApplications } from "../hooks/useApplications";
import { Application, FindApplicationsOptions, ApplicationAPI } from "../api/applications";
import { APPLICATION_TYPE_OPTIONS, BUSINESS_IMPACT_OPTIONS, DEVELOPMENT_PHASE_OPTIONS, getApplicationTypeLabel, getBusinessImpactLabel, getDevelopmentPhaseLabel } from '@/helpers/collections';
import { useAuth } from '@/contexts/AuthContext';
import { useHasPermission } from '@/hooks/useHasPermission';


interface ApplicationsProps {
  isGridLayout: boolean;
  workgroupId: number;
  onNavigate: (route: string, params?: any) => void;
  onRefreshApplications?: (refreshFn: () => void) => void;
}

export function Applications({ isGridLayout, workgroupId, onNavigate, onRefreshApplications }: ApplicationsProps) {
  const { subscription } = useAuth();
  const canCreate = useHasPermission('applications', 'create');
  const canUpdate = useHasPermission('applications', 'update');
  const canDelete = useHasPermission('applications', 'delete');
  const canRestore = useHasPermission('applications', 'restore');
  const canFavourite = useHasPermission('applications', 'favourite');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [favouriteFilter, setFavouriteFilter] = useState<string>('all');
  const [businessImpactFilter, setBusinessImpactFilter] = useState<string>('all');
  const [applicationTypeFilter, setApplicationTypeFilter] = useState<string>('all');
  const [developmentPhaseFilter, setDevelopmentPhaseFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);

  const { data: allApplications, loading, error, refetch } = useApplications({
    workgroupId,
  } as FindApplicationsOptions);

  const getSortDisplayTitle = (sortKey: string) => {
    const sortTitles: Record<string, string> = {
      'name': 'Name',
      'lastActivity': 'Last Activity',
      'riskScore': 'Risk Score'
    };
    return sortTitles[sortKey] || sortKey;
  };

  const { sortedApplications } = useMemo(() => {
    const filtered = allApplications?.filter(app => {
      const searchMatch = searchTerm === '' ||
        app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.technology?.toLowerCase().includes(searchTerm.toLowerCase());

      const statusMatch = statusFilter === 'all' ||
        (statusFilter === 'Active' && app.isactive) ||
        (statusFilter === 'Archived' && app.isarchived);

      const favouriteMatch = favouriteFilter === 'all' ||
        (favouriteFilter === 'favourites' && app.isfavourite) ||
        (favouriteFilter === 'non-favourites' && !app.isfavourite);

      const businessImpactMatch = businessImpactFilter === 'all' ||
        app.businesscriticality === businessImpactFilter.toLowerCase();

      const applicationTypeMatch = applicationTypeFilter === 'all' ||
        app.applicationtype === applicationTypeFilter;

      const developmentPhaseMatch = developmentPhaseFilter === 'all' ||
        app.phase === developmentPhaseFilter;

      return searchMatch && statusMatch && favouriteMatch && businessImpactMatch && applicationTypeMatch && developmentPhaseMatch;
    }) || [];

    const sorted = [...filtered].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'lastActivity':
          aValue = a.lastactivityon || a.createdon;
          bValue = b.lastactivityon || b.createdon;
          break;
        case 'riskScore':
          aValue = a.avg_overall_score || 0;
          bValue = b.avg_overall_score || 0;
          break;
        default:
          aValue = a.name;
          bValue = b.name;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return {
      sortedApplications: sorted
    };
  }, [allApplications, searchTerm, statusFilter, favouriteFilter, businessImpactFilter, applicationTypeFilter, developmentPhaseFilter, sortBy, sortOrder]);

  const itemsPerPage = 9;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedApplications = sortedApplications.slice(startIndex, endIndex);

  if (onRefreshApplications) {
    onRefreshApplications(refetch);
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleFavouriteFilter = (filter: string) => {
    setFavouriteFilter(filter);
    setCurrentPage(1);
  };

  const handleBusinessImpactFilter = (impact: string) => {
    setBusinessImpactFilter(impact);
    setCurrentPage(1);
  };

  const handleApplicationTypeFilter = (type: string) => {
    setApplicationTypeFilter(type);
    setCurrentPage(1);
  };

  const handleDevelopmentPhaseFilter = (phase: string) => {
    setDevelopmentPhaseFilter(phase);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setStatusFilter('all');
    setFavouriteFilter('all');
    setBusinessImpactFilter('all');
    setApplicationTypeFilter('all');
    setDevelopmentPhaseFilter('all');
    setSortBy('name');
    setSortOrder('asc');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const hasActiveFilters = statusFilter !== 'all' ||
    favouriteFilter !== 'all' ||
    businessImpactFilter !== 'all' ||
    applicationTypeFilter !== 'all' ||
    developmentPhaseFilter !== 'all' ||
    searchTerm.length > 0;

  const handleViewProjects = (applicationId: number) => {
    onNavigate('projects', { applicationId });
  };

  const handleEditApplication = (app: Application) => {
    setSelectedApplication(app);
    setEditModalOpen(true);
  };

  const handleArchiveApplication = (app: Application) => {
    setSelectedApplication(app);
    setArchiveModalOpen(true);
  };

  const handleDeleteApplication = (app: Application) => {
    setSelectedApplication(app);
    setDeleteModalOpen(true);
  };

  const handleRestoreApplication = (app: Application) => {
    setSelectedApplication(app);
    setRestoreModalOpen(true);
  };

  const handleToggleFavorite = async (app: Application) => {
    try {
      await ApplicationAPI.favourite(app.id, !app.isfavourite);
      refetch();
    } catch (error) {
    }
  };

  const handleModalSuccess = () => {
    refetch();
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center">Loading applications...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center text-red-600">Error: {error}</div>
      </div>
    );
  }

  const ApplicationCard = ({ app }: { app: Application }) => {
    // Clicking the card does the same as "View Projects".
    const cardLink = {
      onClick: () => handleViewProjects(app.id),
      href: `/projects/${app.id}`
    };

    const gridProps = {
      ...cardLink,
      title: app.name,
      description: app.description || 'No description available',
      badges: [
        {
          label: (app.isarchived ? 'Archived' : app.isactive ? 'Active' : 'Inactive'),
          className: getStatusColor((app.isarchived ? 'Archived' : app.isactive ? 'Active' : 'Inactive'))
        },
        ...(app.isfavourite ? [{
          label: 'Favourite',
          className: 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
        }] : []),
      ],
      metrics: [
        {
          label: `${app.project_count || 0} Projects`,
          value: '',
          icon: <FolderOpen className="w-4 h-4 mr-1" />
        },
        {
          label: `${app.total_scan_count || 0} Scans`,
          value: '',
          icon: <Activity className="w-4 h-4 mr-1" />
        },
        {
          label: `${app.creator_firstname}`,
          value: '',
          icon: <User className="w-4 h-4 mr-1" />
        },
        {
          label: `${app.lastactivityon ? 'Last: ' + new Date(app.lastactivityon).toLocaleDateString() : 'No activity'}`,
          value: '',
          icon: <Calendar className="w-4 h-4 mr-1" />
        }
      ],

      riskScores: {
        overallScore: app.avg_overall_score || 0,
        vulnerabilityScore: app.avg_vulnerability_score || 0,
        licenseScore: app.avg_license_score || 0,
        operationalRisk: app.avg_operational_risk_score || 0,
        technicalDebt: app.avg_technical_debt_score || 0,
        licenseIssues: 0
      },
      actions: [
        ...(canFavourite ? [{
          label: app.isfavourite ? 'Remove from Favourites' : 'Add to Favourites',
          icon: <Heart className={`w-4 h-4 mr-2 ${app.isfavourite ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />,
          onClick: () => handleToggleFavorite(app)
        }] : []),
        {
          label: 'View Projects',
          icon: <Eye className="w-4 h-4 mr-2" />,
          onClick: () => handleViewProjects(app.id)
        },
        {
          label: 'View Report',
          icon: <FileText className="w-4 h-4 mr-2" />,
          onClick: () => onNavigate('application-view-report', { applicationId: app.id })
        },
        ...(canUpdate ? [{
          label: 'Edit Application',
          icon: <Edit className="w-4 h-4 mr-2" />,
          onClick: () => handleEditApplication(app)
        }] : []),
        ...(app.isarchived
          ? (canRestore ? [{
              label: 'Restore Application',
              icon: <ArchiveRestore className="w-4 h-4 mr-2" />,
              onClick: () => handleRestoreApplication(app),
              variant: 'default' as const
            }] : [])
          : (canDelete ? [{
              label: 'Archive Application',
              icon: <Archive className="w-4 h-4 mr-2" />,
              onClick: () => handleArchiveApplication(app),
              variant: 'danger' as const
            }] : [])),
        ...(canDelete ? [{
          label: 'Delete Application',
          icon: <Trash2 className="w-4 h-4 mr-2" />,
          onClick: () => handleDeleteApplication(app),
          variant: 'danger' as const
        }] : [])
      ]
    };

    const listProps = {
      ...cardLink,
      title: app.name,
      description: app.description,
      badges: [
        {
          label: (app.isarchived ? 'Archived' : app.isactive ? 'Active' : 'Inactive'),
          className: getStatusColor((app.isarchived ? 'Archived' : app.isactive ? 'Active' : 'Inactive'))
        },
        ...(app.isfavourite ? [{
          label: 'Favourite',
          className: 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
        }] : []),
      ],
      metrics: [
        {
          label: `${app.project_count || 0}`,
          value: '',
          icon: <FolderOpen className="w-4 h-4 mr-1" />
        },
        {
          label: `${app.total_scan_count || 0}`,
          value: '',
          icon: <Activity className="w-4 h-4 mr-1" />
        },
        {
          label: `${app.creator_firstname}`,
          value: '',
          icon: <User className="w-4 h-4 mr-1" />
        },
        {
          label: `${app.lastactivityon ? new Date(app.lastactivityon).toLocaleDateString() : 'No activity'}`,
          value: '',
          icon: <Calendar className="w-4 h-4 mr-1" />
        }
      ],
      riskScores: {
        overallScore: app.avg_overall_score || 0,
        vulnerabilityScore: app.avg_vulnerability_score || 0,
        licenseScore: app.avg_license_score || 0,
        operationalRisk: app.avg_operational_risk_score || 0,
        technicalDebt: app.avg_technical_debt_score || 0,
        licenseIssues: 0
      },
      actions: [
        ...(canFavourite ? [{
          label: app.isfavourite ? 'Remove from Favourites' : 'Add to Favourites',
          icon: <Heart className={`w-4 h-4 mr-2 ${app.isfavourite ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />,
          onClick: () => handleToggleFavorite(app)
        }] : []),
        {
          label: 'View Projects',
          icon: <Eye className="w-4 h-4 mr-2" />,
          onClick: () => handleViewProjects(app.id)
        },
        {
          label: 'View Report',
          icon: <FileText className="w-4 h-4 mr-2" />,
          onClick: () => onNavigate('application-view-report', { applicationId: app.id })
        },
        ...(canUpdate ? [{
          label: 'Edit Application',
          icon: <Edit className="w-4 h-4 mr-2" />,
          onClick: () => handleEditApplication(app)
        }] : []),
        ...(app.isarchived
          ? (canRestore ? [{
              label: 'Restore Application',
              icon: <ArchiveRestore className="w-4 h-4 mr-2" />,
              onClick: () => handleRestoreApplication(app),
              variant: 'default' as const
            }] : [])
          : (canDelete ? [{
              label: 'Archive Application',
              icon: <Archive className="w-4 h-4 mr-2" />,
              onClick: () => handleArchiveApplication(app),
              variant: 'danger' as const
            }] : [])),
        ...(canDelete ? [{
          label: 'Delete Application',
          icon: <Trash2 className="w-4 h-4 mr-2" />,
          onClick: () => handleDeleteApplication(app),
          variant: 'danger' as const
        }] : [])
      ]
    };

    return (
      <UnifiedCard
        isGridLayout={isGridLayout}
        gridProps={gridProps}
        listProps={listProps}
        className={"gap-0"}
      >
        {isGridLayout ? (
          <>
            {/* Four equal columns keep the details on one line; long labels and badge text wrap inside their column. */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="flex flex-col items-center text-center min-w-0">
                <div className="text-xs text-muted-foreground mb-1 font-medium">Business Criticality</div>
                <Badge className={`${getPriorityColor(app.businesscriticality || 'Medium')} max-w-full whitespace-normal`} variant="outline">
                  {getBusinessImpactLabel(app.businesscriticality) || 'Medium'}
                </Badge>
              </div>
              <div className="flex flex-col items-center text-center min-w-0">
                <div className="text-xs text-muted-foreground mb-1 font-medium">Application Type</div>
                <Badge variant="outline" className="bg-muted max-w-full whitespace-normal">
                  {getApplicationTypeLabel(app.applicationtype) || 'Internal'}
                </Badge>
              </div>
              <div className="flex flex-col items-center text-center min-w-0">
                <div className="text-xs text-muted-foreground mb-1 font-medium">Development Phase</div>
                <Badge variant="outline" className={`${getDevelopmentPhaseColor(app.phase || 'Production')} max-w-full whitespace-normal`}>
                  {getDevelopmentPhaseLabel(app.phase) || 'Production'}
                </Badge>
              </div>
              <div className="flex flex-col items-center text-center min-w-0">
                <div className="text-xs text-muted-foreground mb-1 font-medium">Technology</div>
                <div className="flex flex-wrap justify-center gap-1 max-w-full">
                  {app.technology && app.technology.length > 2 ? (
                    (JSON.parse(app.technology) as string[])?.map((tech, index) => (
                      <Badge key={index} variant="outline" className="text-xs bg-muted max-w-full whitespace-normal">
                        {tech}
                      </Badge>
                    ))
                  ) : (
                    <Badge variant="outline" className="text-xs bg-muted max-w-full whitespace-normal">
                      Not specified
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (<>
          <div className="flex items-center space-x-2 flex-shrink-0">
            <div className="text-center">
              <Badge className={getPriorityColor(app.businesscriticality || 'Medium')} variant="outline" title='Business Criticality'>
                {getBusinessImpactLabel(app.businesscriticality) || 'Medium'}
              </Badge>
            </div>
            <div className="text-center">
              <Badge variant="outline" className="bg-muted" title='Application Type'>
                {getApplicationTypeLabel(app.applicationtype) || 'Internal'}
              </Badge>
            </div>
            <div className="text-center">
              <Badge variant="outline" className={getDevelopmentPhaseColor(app.phase || 'Production')} title='Development Phase'>
                {getDevelopmentPhaseLabel(app.phase) || 'Production'}
              </Badge>
            </div>
          </div>


        </>)}
      </UnifiedCard>
    );
  };

  return (
    <div className="p-4 space-y-2 max-w-full" style={{ paddingTop: "10px" }}>
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center gap-2 justify-center sm:justify-end">
          <Badge
            variant="secondary"
            className="cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-xs whitespace-nowrap"
            onClick={() => clearAllFilters()}
            title="Click to clear all filters"
          >
            <span className="whitespace-nowrap">{allApplications?.length || 0} Total</span>
          </Badge>
          <Badge
            className={`cursor-pointer transition-colors flex-shrink-0 ${statusFilter === 'Active'
              ? 'bg-green-200 text-green-900 border-green-300 hover:bg-green-300 dark:bg-green-800 dark:text-green-100'
              : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200'
              }`}
            onClick={() => handleStatusFilter(statusFilter === 'Active' ? 'all' : 'Active')}
            title="Click to filter by Active status"
          >
            <span className="text-xs sm:text-sm whitespace-nowrap">{allApplications?.filter((app: Application) => app.isactive).length || 0}</span>
            <span className="whitespace-nowrap">Active</span>
          </Badge>
          <Badge
            className={`cursor-pointer transition-colors flex-shrink-0 ${favouriteFilter === 'favourites'
              ? 'bg-red-200 text-red-900 border-red-300 hover:bg-red-300 dark:bg-red-800 dark:text-red-100'
              : 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-200'
              }`}
            onClick={() => handleFavouriteFilter(favouriteFilter === 'favourites' ? 'all' : 'favourites')}
            title="Click to filter by Favourites"
          >
            <Heart className="w-3 h-3 mr-1 flex-shrink-0" />
            <span className="text-xs sm:text-sm whitespace-nowrap">{allApplications?.filter((app: Application) => app.isfavourite).length || 0}</span>
            <span className="whitespace-nowrap">Favourite</span>
          </Badge>
          <Badge
            className={`cursor-pointer transition-colors flex-shrink-0 ${statusFilter === 'Archived'
              ? 'bg-yellow-200 text-yellow-900 border-yellow-300 hover:bg-yellow-300 dark:bg-yellow-800 dark:text-yellow-100'
              : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-200'
              }`}
            onClick={() => handleStatusFilter(statusFilter === 'Archived' ? 'Active' : 'Archived')}
            title="Click to filter by Archived status"
          >
            <span className="text-xs sm:text-sm whitespace-nowrap">{allApplications?.filter((app: Application) => app.isarchived).length || 0}</span>
            <span className="whitespace-nowrap">Archived</span>
          </Badge>
        </div>

        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 min-w-0 flex-1">
            <div className="relative w-full xs:flex-1 sm:max-w-xs lg:max-w-sm xl:max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground flex-shrink-0 z-10" />
              <Input
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 w-full bg-white rounded-lg shadow-sm border-border text-sm"
                maxLength={200}
              />
            </div>
            <div className="relative w-full flex xs:flex-1 sm:max-w-xs lg:max-w-sm xl:max-w-md gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className={`bg-white rounded-lg shadow-sm border-border flex-shrink-0 ${hasActiveFilters ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : ''
                      }`}
                  >
                    <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="whitespace-nowrap">Filters</span>
                    {hasActiveFilters && (
                      <Badge variant="secondary" className="ml-2 text-xs flex-shrink-0">
                        {[statusFilter, businessImpactFilter, favouriteFilter, applicationTypeFilter, developmentPhaseFilter].filter(f => f !== 'all').length}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-100 max-w-[calc(100vw-1rem)] max-h-[70vh] overflow-y-auto">
                  <DropdownMenuLabel>Filter Applications</DropdownMenuLabel>
                  <DropdownMenuSeparator />

                  <div className="px-2 py-1">
                    <div className="text-sm font-medium text-muted-foreground mb-2">Status</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
                      {['all', 'Active', 'Archived'].map((status) => (
                        <DropdownMenuItem
                          key={status}
                          onClick={() => handleStatusFilter(status)}
                          className={`text-sm justify-between ${statusFilter === status
                            ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                            : ''
                            }`}
                        >
                          <span>{status === 'all' ? 'All' : status}</span>
                          {statusFilter === status && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />}
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </div>

                  <DropdownMenuSeparator />

                  <div className="px-2 py-1">
                    <div className="text-sm font-medium text-muted-foreground mb-2">Business Criticality</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
                      {[{ value: 'all', label: 'All' }, ...BUSINESS_IMPACT_OPTIONS].map((impact) => (
                        <DropdownMenuItem
                          key={impact.value}
                          onClick={() => handleBusinessImpactFilter(impact.value)}
                          className={`text-sm justify-between ${businessImpactFilter === impact.value
                            ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                            : ''
                            }`}
                        >
                          <span>{impact.value === 'all' ? 'All' : impact.label}</span>
                          {businessImpactFilter === impact.value && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />}
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </div>

                  <DropdownMenuSeparator />

                  <div className="px-2 py-1">
                    <div className="text-sm font-medium text-muted-foreground mb-2">Application Type</div>
                    <div className="grid grid-cols-1 gap-1">
                      {[{ value: 'all', label: 'All Types' }, ...APPLICATION_TYPE_OPTIONS].map((type) => (
                        <DropdownMenuItem
                          key={type.value}
                          onClick={() => handleApplicationTypeFilter(type.value)}
                          className={`text-sm justify-between ${applicationTypeFilter === type.value
                            ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                            : ''
                            }`}
                        >
                          <span>{type.value === 'all' ? 'All Types' : type.label}</span>
                          {applicationTypeFilter === type.value && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />}
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </div>

                  <DropdownMenuSeparator />

                  <div className="px-2 py-1">
                    <div className="text-sm font-medium text-muted-foreground mb-2">Development Phase</div>
                    <div className="grid grid-cols-1 gap-1">
                      {[{ value: 'all', label: 'All Phases' }, ...DEVELOPMENT_PHASE_OPTIONS].map((phase) => (
                        <DropdownMenuItem
                          key={phase.value}
                          onClick={() => handleDevelopmentPhaseFilter(phase.value)}
                          className={`text-sm justify-between ${developmentPhaseFilter === phase.value
                            ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                            : ''
                            }`}
                        >
                          <span>{phase.value === 'all' ? 'All Phases' : phase.label}</span>
                          {developmentPhaseFilter === phase.value && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />}
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </div>

                  <DropdownMenuSeparator />

                  {hasActiveFilters && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={clearAllFilters}
                        className="text-sm text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Clear All Filters
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className={`bg-white rounded-lg shadow-sm border-border ${sortBy !== 'name' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : ''
                      }`}
                  >
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    Sort: {getSortDisplayTitle(sortBy)}
                    {sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleSort('name')}>Name</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('lastActivity')}>Last Activity</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('riskScore')}>Risk Score</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearAllFilters}
                  className="text-muted-foreground hover:text-foreground flex-shrink-0"
                >
                  <X className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Clear All</span>
                  <span className="sm:hidden">Clear</span>
                </Button>
              )}
            </div>

          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-4 xl:shrink-0">

            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {canCreate && isSubscriptionValid(subscription?.validupto || null) && (
                <Button
                  onClick={() => onNavigate('new-application')}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg shadow-lg shadow-primary/20 flex-shrink-0 whitespace-nowrap"
                >
                  <Plus className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>New Application</span>
                </Button>
              )}
            </div>
          </div>
        </div>

      </div>

      <div className={isGridLayout ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-4" : "space-y-4"}>
        {paginatedApplications.map((app) => (
          <ApplicationCard key={app.id} app={app} />
        ))}
      </div>

      {paginatedApplications.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {hasActiveFilters
              ? "No applications found matching your filters"
              : "No applications found"
            }
          </p>
          {hasActiveFilters && (
            <Button
              variant="outline"
              onClick={clearAllFilters}
              className="mt-4"
            >
              Clear all filters
            </Button>
          )}
        </div>
      )}

      <Paginator
        totalItems={sortedApplications.length}
        currentPage={currentPage}
        perPage={itemsPerPage}
        onPageChange={handlePageChange}
        className="mt-6"
      />

      <ApplicationForm
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        application={selectedApplication}
        onSuccess={handleModalSuccess}
      />

      <ArchiveApplication
        isOpen={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        application={selectedApplication}
        onSuccess={handleModalSuccess}
      />

      <DeleteApplication
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        application={selectedApplication}
        onSuccess={handleModalSuccess}
      />

      <RestoreApplication
        isOpen={restoreModalOpen}
        onClose={() => setRestoreModalOpen(false)}
        application={selectedApplication}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
}